import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { 
    FaFilter, FaArrowRight, FaArrowLeft, FaTrash, FaPlusCircle, 
    FaObjectGroup, FaExchangeAlt, FaTimes
} from 'react-icons/fa';

// Importiamo i nuovi modali condivisi
import { SuccessModal, ErrorModal, ConfirmModal, InfoModal } from './SharedComponents/Modals';

const MergeTest = ({ auth, onSuccess }) => {
    const navigate = useNavigate();

    // --- COLORI THEME ---
    const theme = {
        primary: '#0C4A76',
        secondary: '#11A0D9',
        accent: '#F2962B',
        bg: '#f8f9fa'
    };

    const roundBtnStyle = {
        width: '45px', height: '45px', borderRadius: '50%', 
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0
    };

    // --- STATI DATI ---
    const [tenants, setTenants] = useState([]);
    const [classes, setClasses] = useState([]);
    const [groups, setGroups] = useState([]); 
    
    // --- STATI SELEZIONE E FILTRI ---
    const [filters, setFilters] = useState({
        tenant_id: auth.role === 'superadmin' ? '' : auth.tenant_id,
        class_id: ''
    });

    const [sourceTests, setSourceTests] = useState([]); 
    const [selectedTests, setSelectedTests] = useState([]); 

    // --- CONFIGURAZIONE NUOVO TEST ---
    const [newConfig, setNewConfig] = useState({
        name: '', subject: '', category: '', duration: 30, pass_threshold: 60, target_group_id: ''
    });

    const [loading, setLoading] = useState(false);
    
    // --- GESTIONE STATO MODALI ---
    const [modalState, setModalState] = useState({
        type: null, // 'error', 'success', 'confirm', 'info'
        message: '',
        title: '',
        confirmAction: null,
        isDestructive: false,
        confirmColor: '', // Aggiunto per gestire il colore custom (verde per create)
        hideButtons: false,
        autoClose: 0
    });

    // Helper per chiudere i modali
    const closeModal = () => {
        const type = modalState.type;
        setModalState({ type: null, message: '', title: '' });
        
        // Se era un successo, naviga via
        if (type === 'success') {
            if (onSuccess) onSuccess();
            navigate('/tests');
        }
    };

    // 1. Caricamento Iniziale
    useEffect(() => {
        if (auth.role === 'superadmin') {
            axios.get('/api_tenants.php').then(res => { if (res.data.success) setTenants(res.data.data); });
        }
    }, [auth.role]);

    // 2. Caricamento Dati
    useEffect(() => {
        if (!filters.tenant_id && auth.role === 'superadmin') return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    action: 'get_data', tenant_id: filters.tenant_id, class_id: filters.class_id
                });
                const res = await axios.get(`/api_merge_tests.php?${params.toString()}`);
                if (res.data.success) {
                    setClasses(res.data.classes);
                    setGroups(res.data.groups);
                    let available = res.data.tests.filter(t => !selectedTests.find(s => s.id === t.id));
                    
                    if (auth.role === 'professor') {
                        available = available.filter(t => t.created_by == auth.user_id);
                    }
                    setSourceTests(available);
                }
            } catch (err) {
                setModalState({ type: 'error', title: 'Errore Caricamento', message: 'Impossibile recuperare i dati dal server.' });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [filters.tenant_id, filters.class_id, auth.role, auth.user_id, selectedTests.length]);

    // --- HANDLERS SPOSTAMENTO ---
    const moveToSelected = (test) => {
        setSelectedTests([...selectedTests, test]);
        setSourceTests(sourceTests.filter(t => t.id !== test.id));
    };
    const moveToSource = (test) => {
        setSourceTests([...sourceTests, test]);
        setSelectedTests(selectedTests.filter(t => t.id !== test.id));
    };
    const moveAllToSelected = () => {
        setSelectedTests([...selectedTests, ...sourceTests]);
        setSourceTests([]);
    };
    const moveAllToSource = () => {
        setSourceTests([...sourceTests, ...selectedTests]);
        setSelectedTests([]);
    };

    // --- PREPARAZIONE AZIONE ---
    const initiateProcess = (mode) => {
        if (selectedTests.length < 2) {
            setModalState({ type: 'info', title: 'Selezione Insufficiente', message: 'Seleziona almeno 2 test da unire.' });
            return;
        }
        if (!newConfig.name || !newConfig.subject || !newConfig.target_group_id || !filters.tenant_id) {
            setModalState({ type: 'error', title: 'Dati Mancanti', message: 'Compila tutti i campi obbligatori (Nome, Materia, Gruppo).' });
            return;
        }
        
        // Configura il modale di conferma
        setModalState({
            type: 'confirm',
            title: mode === 'merge' ? 'Conferma Unione ed Eliminazione' : 'Conferma Creazione',
            isDestructive: mode === 'merge',
            confirmColor: mode === 'merge' ? 'btn-danger' : 'btn-success', // Rosso per merge, Verde per create
            confirmAction: () => executeProcess(mode),
            message: mode === 'merge' ? (
                <>
                    Stai per unire <strong>{selectedTests.length}</strong> test nel nuovo test <strong>"{newConfig.name}"</strong>.<br/>
                    <span className="text-danger fw-bold">I test originali verranno eliminati permanentemente.</span>
                </>
            ) : (
                <>
                    Verrà creato il test <strong>"{newConfig.name}"</strong> copiando le domande da <strong>{selectedTests.length}</strong> test.<br/>
                    I test originali rimarranno intatti.
                </>
            )
        });
    };

    // --- ESECUZIONE REALE ---
    const executeProcess = async (mode) => {
        setModalState({ type: null }); // Chiudi conferma
        setLoading(true);

        try {
            const payload = {
                mode: mode, 
                source_ids: selectedTests.map(t => t.id),
                new_test: { ...newConfig, tenant_id: filters.tenant_id, threshold: newConfig.pass_threshold }
            };

            const res = await axios.post('/api_merge_tests.php?action=merge', payload);
            
            if (res.data.success) {
                // Successo: Configuro il modale di successo
                setModalState({
                    type: 'success',
                    title: 'Operazione Completata',
                    message: mode === 'merge' ? "Test uniti ed eliminati con successo!" : "Nuovo test creato con successo!",
                    hideButtons: false, // Lasciamo i bottoni visibili per permettere all'utente di leggere
                    autoClose: 0 // Nessun auto-close per sicurezza (l'utente deve confermare di aver letto)
                });
            } else {
                throw new Error(res.data.message);
            }
        } catch (err) {
            setModalState({ 
                type: 'error', 
                title: 'Errore Operazione', 
                message: err.response?.data?.message || err.message || "Errore sconosciuto" 
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card shadow-lg border-0">
            {/* HEADER */}
            <div className="card-header text-white p-4" style={{ backgroundColor: theme.primary }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="mb-1 fw-bold"><FaObjectGroup className="me-2"/> Unisci Test</h4>
                        <p className="mb-0 fw-light text-white-50">Unisci più test esistenti in un unico nuovo test o crea una copia unificata.</p>
                    </div>
                    {/* Tasto Chiudi Rimosso come da richiesta precedente, ma se lo volessi è qui: */}
                    {/* <button className="btn btn-sm btn-outline-light border-0" onClick={() => navigate('/tests')}><FaTimes size={20}/></button> */}
                </div>
            </div>

            <div className="card-body bg-light p-4">
                
                {/* --- RENDERIZZAZIONE CONDIZIONALE DEI MODALI --- */}
                <ErrorModal 
                    show={modalState.type === 'error'} 
                    onClose={closeModal} 
                    title={modalState.title} 
                    message={modalState.message} 
                />
                
                <SuccessModal 
                    show={modalState.type === 'success'} 
                    onClose={closeModal} 
                    onConfirm={closeModal}
                    title={modalState.title} 
                    message={modalState.message}
                    hideButtons={modalState.hideButtons}
                    autoCloseDuration={modalState.autoClose}
                    confirmText="Torna alla Lista"
                />

                <InfoModal 
                    show={modalState.type === 'info'} 
                    onClose={closeModal} 
                    title={modalState.title} 
                    message={modalState.message} 
                />

                <ConfirmModal 
                    show={modalState.type === 'confirm'} 
                    onClose={closeModal} 
                    onConfirm={modalState.confirmAction}
                    title={modalState.title} 
                    message={modalState.message}
                    isDestructive={modalState.isDestructive}
                    confirmColor={modalState.confirmColor}
                    confirmText={modalState.isDestructive ? 'Procedi e Unisci' : 'Crea Test'}
                />

                {/* --- CONTENUTO PRINCIPALE --- */}
                
                {/* SEZIONE 1: CONFIGURAZIONE */}
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-4">
                        <h5 className="fw-bold mb-4 border-bottom pb-2" style={{color: theme.primary}}>1. Dati Nuovo Test</h5>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Nome Test *</label>
                                <input className="form-control" value={newConfig.name} onChange={e => setNewConfig({...newConfig, name: e.target.value})} placeholder="Es. Esame Finale Unificato"/>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Materia *</label>
                                <input className="form-control" value={newConfig.subject} onChange={e => setNewConfig({...newConfig, subject: e.target.value})}/>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Categoria</label>
                                <input className="form-control" value={newConfig.category} onChange={e => setNewConfig({...newConfig, category: e.target.value})}/>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Azienda (Tenant) *</label>
                                {auth.role === 'superadmin' ? (
                                    <select className="form-select" value={filters.tenant_id} onChange={e => setFilters({...filters, tenant_id: e.target.value, class_id: ''})}>
                                        <option value="">Seleziona...</option>
                                        {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                ) : (
                                    <input className="form-control bg-light" value="La tua Azienda" disabled />
                                )}
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Gruppo Destinazione *</label>
                                <select className="form-select" value={newConfig.target_group_id} onChange={e => setNewConfig({...newConfig, target_group_id: e.target.value})} disabled={!filters.tenant_id}>
                                    <option value="">Seleziona...</option>
                                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Durata (min)</label>
                                <input type="number" className="form-control" value={newConfig.duration} onChange={e => setNewConfig({...newConfig, duration: e.target.value})}/>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Soglia Superamento (%)</label>
                                <input type="number" className="form-control" value={newConfig.pass_threshold} onChange={e => setNewConfig({...newConfig, pass_threshold: e.target.value})}/>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SEZIONE 2: SELEZIONE TEST */}
                <h5 className="fw-bold mb-3 ms-1" style={{color: theme.primary}}>2. Selezione Test da Unire</h5>
                <div className="row g-0 mb-4 align-items-stretch" style={{minHeight: '400px'}}>
                    
                    {/* LEFT */}
                    <div className="col-md-5">
                        <div className="card h-100 border shadow-sm">
                            <div className="card-header text-white d-flex justify-content-between align-items-center py-3" style={{backgroundColor: theme.secondary}}>
                                <span className="fw-bold"><FaFilter className="me-2"/> Disponibili</span>
                                <select className="form-select w-50 border-0 bg-white bg-opacity-25 text-white" value={filters.class_id} onChange={e => setFilters({...filters, class_id: e.target.value})}>
                                    <option value="" className="text-dark">Tutte le classi</option>
                                    {classes.map(c => <option key={c.id} value={c.id} className="text-dark">{c.name}</option>)}
                                </select>
                            </div>
                            <div className="card-body p-0 bg-white" style={{height: '350px', overflowY: 'auto'}}>
                                {loading && <div className="p-4 text-center text-muted">Caricamento test...</div>}
                                {!loading && sourceTests.length === 0 && <div className="p-4 text-center text-muted">Nessun test disponibile trovato.</div>}
                                <ul className="list-group list-group-flush">
                                    {sourceTests.map(t => (
                                        <li key={t.id} 
                                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3"
                                            style={{cursor: 'pointer'}}
                                            onDoubleClick={() => moveToSelected(t)}
                                        >
                                            <div className="overflow-hidden me-2">
                                                <div className="fw-bold text-dark text-truncate">{t.name}</div>
                                                <small className="text-muted d-block text-truncate">{t.subject} • {t.category}</small>
                                            </div>
                                            <button className="btn btn-outline-primary shadow-sm" style={roundBtnStyle} onClick={() => moveToSelected(t)}><FaArrowRight/></button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* CONTROLS */}
                    <div className="col-md-2 d-flex flex-column justify-content-center align-items-center gap-3 py-3 py-md-0 bg-transparent">
                        <button className="btn btn-secondary shadow-sm" style={roundBtnStyle} onClick={moveAllToSelected} title="Sposta tutti a destra"><FaArrowRight/></button>
                        <button className="btn btn-secondary shadow-sm" style={roundBtnStyle} onClick={moveAllToSource} title="Reset"><FaArrowLeft/></button>
                    </div>

                    {/* RIGHT */}
                    <div className="col-md-5">
                        <div className="card h-100 border shadow-sm" style={{borderColor: theme.accent}}>
                            <div className="card-header text-white d-flex justify-content-between align-items-center py-3" style={{backgroundColor: theme.accent}}>
                                <span className="fw-bold"><FaPlusCircle className="me-2"/> Selezionati ({selectedTests.length})</span>
                            </div>
                            <div className="card-body p-0" style={{height: '350px', overflowY: 'auto', backgroundColor: '#fff8e1'}}>
                                {selectedTests.length === 0 && <div className="h-100 d-flex flex-column justify-content-center align-items-center text-muted opacity-50"><FaObjectGroup size={40} className="mb-2"/><span>Trascina o seleziona test qui</span></div>}
                                <ul className="list-group list-group-flush">
                                    {selectedTests.map(t => (
                                        <li key={t.id} 
                                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center bg-transparent py-3 border-bottom-0 border-top"
                                            style={{cursor: 'pointer'}}
                                            onDoubleClick={() => moveToSource(t)}
                                        >
                                            <div className="overflow-hidden me-2"><div className="fw-bold text-dark text-truncate">{t.name}</div></div>
                                            <button className="btn btn-outline-danger shadow-sm" style={roundBtnStyle} onClick={() => moveToSource(t)}><FaTrash/></button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* FOOTER ACTIONS */}
                <div className="d-flex justify-content-end align-items-center gap-3 pt-4 border-top">
                    <button className="btn btn-secondary px-4 py-2" onClick={() => navigate('/tests')}>Annulla e Torna Indietro</button>
                    <button className="btn btn-success px-4 py-2 fw-bold text-white shadow-sm" onClick={() => initiateProcess('create')} disabled={loading} style={{backgroundColor: theme.secondary, borderColor: theme.secondary}}><FaPlusCircle className="me-2"/> CREA NUOVO</button>
                    <button className="btn btn-warning px-4 py-2 fw-bold text-white shadow-sm" onClick={() => initiateProcess('merge')} disabled={loading} style={{backgroundColor: theme.accent, borderColor: theme.accent}}><FaExchangeAlt className="me-2"/> UNISCI (Elimina Orig.)</button>
                </div>
            </div>
        </div>
    );
};

export default MergeTest;